import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { ClientAddressModel, NewClientAddressModel } from "services/api/cplapi/CPLAPI";
import { IClientPortalAccountContactDetailsService } from "services/ClientPortalAccountContactDetailsService";
import { container } from "setup/dependency/inversify.config";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { RootState } from "setup/redux/RootReducer";

type ClientAddressesProps = {
  LoadingClientAddressDetails: boolean;
  ClientAddressDetails: ClientAddressModel[];
};

const initialState: ClientAddressesProps = {
  LoadingClientAddressDetails: false,
  ClientAddressDetails: new Array<ClientAddressModel>(),
};

export const ClientAddressesSlice = createSlice({
  name: "ClientAddresses",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getClientAddressDetails.pending, (state) => {
        state.LoadingClientAddressDetails = true;
      })
      .addCase(getClientAddressDetails.fulfilled, (state, action) => {
        state.LoadingClientAddressDetails = false;
        state.ClientAddressDetails = action.payload;
      })
      .addCase(getClientAddressDetails.rejected, (state) => {
        state.LoadingClientAddressDetails = false;
        state.ClientAddressDetails = new Array<ClientAddressModel>();
      })
      .addCase(updateClientAddressDetails.pending, (state) => {
        state.LoadingClientAddressDetails = true;
      })
      .addCase(updateClientAddressDetails.fulfilled, (state) => {
        state.LoadingClientAddressDetails = false;
        toast.success("Successfully updated address");
      })
      .addCase(updateClientAddressDetails.rejected, (state) => {
        state.LoadingClientAddressDetails = false;
      })
      .addCase(deleteClientAddress.pending, (state) => {
        state.LoadingClientAddressDetails = true;
      })
      .addCase(deleteClientAddress.fulfilled, (state) => {
        state.LoadingClientAddressDetails = false;
        toast.success("Successfully deleted address");
      })
      .addCase(deleteClientAddress.rejected, (state) => {
        state.LoadingClientAddressDetails = false;
      })
      .addCase(createNewClientAddress.pending, (state) => {
        state.LoadingClientAddressDetails = false;
      })
      .addCase(createNewClientAddress.rejected, (state) => {
        state.LoadingClientAddressDetails = false;
      })
      .addCase(createNewClientAddress.fulfilled, (state) => {
        state.LoadingClientAddressDetails = false;
        toast.success("Successfully added address");
      });
  },
});

export const getClientAddressDetails = createAsyncThunk<ClientAddressModel[], { clientID: string }>(
  "ClientAddresses/GetClientAddressDetails",
  async ({ clientID }) => {
    const clientPortalAccountContactDetailsService = container.get<IClientPortalAccountContactDetailsService>(
      SERVICE_TYPES.ClientPortalAccountContactDetailsService
    );
    return await clientPortalAccountContactDetailsService.getClientAddressDetails(clientID);
  }
);

export const createNewClientAddress = createAsyncThunk<
  void,
  { newClientAddress: NewClientAddressModel },
  { state: RootState }
>("ClientAddresses/CreateNewClientAddress", async ({ newClientAddress }, thunkAPI) => {
  const clientPortalAccountContactDetailsService = container.get<IClientPortalAccountContactDetailsService>(
    SERVICE_TYPES.ClientPortalAccountContactDetailsService
  );
  await clientPortalAccountContactDetailsService.createClientAddress(newClientAddress);
  const state = thunkAPI.getState();
  thunkAPI.dispatch(getClientAddressDetails({ clientID: state.clientContactDetails.ClientID }));
});

export const updateClientAddressDetails = createAsyncThunk<
  void,
  { clientAddressModel: ClientAddressModel },
  { state: RootState }
>("ClientAddresses/UpdateClientAddressDetails", async ({ clientAddressModel }, thunkAPI) => {
  const clientPortalAccountContactDetailsService = container.get<IClientPortalAccountContactDetailsService>(
    SERVICE_TYPES.ClientPortalAccountContactDetailsService
  );
  const state = thunkAPI.getState();
  await clientPortalAccountContactDetailsService.updateClientAddress(clientAddressModel);
  thunkAPI.dispatch(getClientAddressDetails({ clientID: state.clientContactDetails.ClientID }));
});

export const deleteClientAddress = createAsyncThunk<void, { clientAddressID: string }, { state: RootState }>(
  "ClientAddresses/DeleteClientAddress",
  async ({ clientAddressID }, thunkAPI) => {
    const clientPortalAccountContactDetailsService = container.get<IClientPortalAccountContactDetailsService>(
      SERVICE_TYPES.ClientPortalAccountContactDetailsService
    );
    const state = thunkAPI.getState();
    await clientPortalAccountContactDetailsService.deleteClientAddress(clientAddressID);
    thunkAPI.dispatch(getClientAddressDetails({ clientID: state.clientContactDetails.ClientID }));
  }
);

export const selectClientAddressDetails = (state: RootState) => {
  return state.clientAddresses.ClientAddressDetails;
};

export const selectLoadingClientAddressDetails = (state: RootState) => {
  return state.clientAddresses.LoadingClientAddressDetails;
};

export const selectNewAddress = (state: RootState) => {
  const model = new NewClientAddressModel({ address: "", postCode: "", description: "" });
  model.clientAccountNumber = state.clientContactDetails.ClientID;
  return model;
};

export const selectHasDifferentBillingAddressSet = (addressID: string) => (state: RootState) => {
  return state.clientAddresses.ClientAddressDetails.some((i) => i.billingAddress && i.clientAddressID != addressID);
};

export default ClientAddressesSlice.reducer;
